import {parsePhoneNumber} from 'libphonenumber-js';
import {useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {postTextSend, postTextVerify} from '../../../api/auth';
import {useUserApi} from '../../../api/user';
import {showVerificationPages, secondsToWaitForSendingNewCode} from '../../../constants';
import {delay, maskPhoneNumber} from '../../../utils/miscellaneous';

/**
 * React hook for fetching user data needed for phone number verification.
 *
 * @returns {Object} - Object containing the masked phone number and user email.
 */
export const useUserDataFetcher = () => {
  const [maskedPhoneNumber, setMaskedPhoneNumber] = useState('');
  const [userEmail, setUserEmail] = useState('');

  const userData = useUserApi();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      const userDataReq = await userData.getUserData();
      if (userDataReq.status !== 200) return;

      const isVerified = userDataReq.data.is_phone_verified;
      if (!showVerificationPages || isVerified) {
        navigate('/demographics');
        return;
      }

      const phoneNumber = userDataReq.data.phone_number;
      if (phoneNumber === null || phoneNumber === '') {
        navigate('/verify-email');
        return;
      }

      // Get the full phone number
      const fullPhoneNumber = parsePhoneNumber(userDataReq.data.phone_number).number;
      setMaskedPhoneNumber(maskPhoneNumber(fullPhoneNumber));
      setUserEmail(userDataReq.data.email);
    };
    fetchData();
  }, []);

  return {maskedPhoneNumber, email: userEmail};
};

/**
 * React hook for handling phone number verification logic.
 *
 * @param {string} email - The user's email.
 * @param {string} maskedPhoneNumber - The masked phone number for display.
 * @returns {Object} - Object containing verification-related states and functions.
 */
export const useVerificationLogic = (email, maskedPhoneNumber) => {
  const navigate = useNavigate();
  const onlySendSMSOnce = useRef(null);
  const {state} = useLocation();
  const {redirectionRoute = undefined} = state || {};

  const userData = useUserApi();
  const [sendNewCodeActivated, setSendNewCodeActivated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [responseReturned, setResponseReturned] = useState(false);
  const [incorrect, setIncorrect] = useState(false);
  const [code, setCode] = useState('');

  useEffect(() => {
    const firstSending = async () => {
      try {
        if (onlySendSMSOnce.current === null) {
          onlySendSMSOnce.current = true;
          await postTextSend(email);
          setResponseReturned(true);
          setIsLoading(false);
          delay(secondsToWaitForSendingNewCode).then(() => {
            setSendNewCodeActivated(true);
          });
        }
      } catch (error) {
        // this conditional would be neccessary if we could in some way show the errors in the frontend
        if (error.response.status !== 401) return;
        navigate('/');
        return;
      }
    };

    if (maskedPhoneNumber === '' || email === '') return;
    setResponseReturned(true);
    setIsLoading(false);
    firstSending();
  }, [maskedPhoneNumber, email]);

  const handleTextSend = () => {
    setIncorrect(false);
    setCode('');
    setSendNewCodeActivated(false);
    delay(secondsToWaitForSendingNewCode).then(() => {
      setSendNewCodeActivated(true);
    });
    postTextSend(email).catch(error => {
      if (error.response.status !== 401) return;
      navigate('/');
    });
  };

  const handleTextVerify = () => {
    postTextVerify(email, code)
      .catch(error => {
        if (error.response) {
          if (error.response.status === 403) {
            setIncorrect(true);
          }
          console.log(error.response);
          if (error.response.status === 409) {
            setIncorrect(false);
            navigate(redirectionRoute || '/demographics');
          }
        }
      })
      .then(res => {
        if (res?.status === 200) {
          setIncorrect(false);
          navigate(redirectionRoute || '/demographics');
        }
      });
  };

  const skipPhoneVerification = async () => {
    //TODO: adding the last time checked thing here
    try {
      await userData.updateTimeUserWasPromptedForPhoneVerification();
    } catch {}
    navigate(redirectionRoute || '/demographics');
  };

  return {
    code,
    setCode,
    incorrect,
    isLoading,
    responseReturned,
    handleTextSend,
    handleTextVerify,
    sendNewCodeActivated,
    skipPhoneVerification,
  };
};
